import React, { createContext, useContext, useState, useMemo, useCallback } from 'react';

const TabsContext = createContext(null);

export function Tabs({ defaultValue, value, onValueChange, className = '', children, ...rest }) {
  const [internal, setInternal] = useState(defaultValue);
  const isControlled = value !== undefined;
  const active = isControlled ? value : internal;

  const setActive = useCallback(
    (v) => {
      if (!isControlled) setInternal(v);
      if (onValueChange) onValueChange(v);
    },
    [isControlled, onValueChange]
  );

  const ctx = useMemo(() => ({ active, setActive }), [active, setActive]);

  return (
    <TabsContext.Provider value={ctx}>
      <div className={className} {...rest}>
        {children}
      </div>
    </TabsContext.Provider>
  );
}

export function TabsList({ className = '', children, ...rest }) {
  return (
    <div role="tablist" className={className} {...rest}>
      {children}
    </div>
  );
}

export function TabsTrigger({ value, className = '', children, ...rest }) {
  const ctx = useContext(TabsContext);
  if (!ctx) throw new Error('TabsTrigger must be used within Tabs');
  const isActive = ctx.active === value;
  const dataState = isActive ? 'active' : 'inactive';

  return (
    <button
      type="button"
      role="tab"
      aria-selected={isActive}
      data-state={dataState}
      onClick={() => ctx.setActive(value)}
      className={className}
      {...rest}
    >
      {children}
    </button>
  );
}

export function TabsContent({ value, className = '', children, ...rest }) {
  const ctx = useContext(TabsContext);
  if (!ctx) throw new Error('TabsContent must be used within Tabs');
  if (ctx.active !== value) return null;
  return (
    <div role="tabpanel" data-state="active" className={className} {...rest}>
      {children}
    </div>
  );
}
